import type { components, PaigeClient } from '@paige/api-client';
import { tenantHeader, unwrap } from './http';

export type MobileSender = components['schemas']['MobileSender'];
export type MobileSenderList = components['schemas']['MobileSenderList'];
export type MobileWaitingList = components['schemas']['MobileWaitingList'];
export type MobileAdminConnect = components['schemas']['MobileAdminConnect'];
export type DeviceConnectCode = components['schemas']['DeviceConnectCode'];
export type MobileInviteHandoff = components['schemas']['MobileInviteHandoff'];

/**
 * Paige Mobile from the web app (docs/engineering/mobile-capture.md, section 13): the company's mobile
 * senders for People (admins: remove one, send a new invite, sign a phone out), the admin's sign-in for
 * the app (`/m/admin`), and the invite page's hand-off of its token for Play's install referrer.
 */
export interface MobileApi {
  senders(tenant: string): Promise<MobileSenderList>;
  /** Colleagues on the company's own email domain who wait in Paige Mobile; nobody else is ever "waiting" here. */
  waiting(tenant: string): Promise<MobileWaitingList>;
  remove(tenant: string, senderId: string): Promise<void>;
  resend(tenant: string, senderId: string): Promise<MobileSender>;
  signOutPhone(tenant: string, senderId: string, phoneId: string): Promise<void>;
  /** Adds an address to a job as a sender; Paige mails them the invite their phone opens (4.6). */
  addToJob(tenant: string, jobId: string, email: string): Promise<void>;
  /** The app's PKCE challenge for an admin of the company; the answer is the app's redirect with a one-time code. */
  connectApp(tenant: string, start: MobileAdminConnect): Promise<DeviceConnectCode>;
  /** The invite token swapped for a one-hour handle, for Play's install referrer. */
  handOff(token: string): Promise<MobileInviteHandoff>;
}

export function mobileApi(client: PaigeClient): MobileApi {
  return { ...senderCalls(client), ...pageCalls(client) };
}

async function waitingOf(client: PaigeClient, tenant: string): Promise<MobileWaitingList> {
  return unwrap(
    await client.GET('/company/mobile-senders/waiting', {
      params: { header: tenantHeader(tenant) },
    }),
  ) as MobileWaitingList;
}

/** People's: the company's senders and what an admin does to one. */
function senderCalls(client: PaigeClient): Omit<MobileApi, 'connectApp' | 'handOff'> {
  return {
    async senders(tenant) {
      return unwrap(
        await client.GET('/company/mobile-senders', { params: { header: tenantHeader(tenant) } }),
      ) as MobileSenderList;
    },
    waiting: (tenant) => waitingOf(client, tenant),
    async remove(tenant, senderId) {
      unwrap(
        await client.DELETE('/company/mobile-senders/{senderId}', {
          params: { header: tenantHeader(tenant), path: { senderId } },
        }),
      );
    },
    async resend(tenant, senderId) {
      return unwrap(
        await client.POST('/company/mobile-senders/{senderId}/invite', {
          params: { header: tenantHeader(tenant), path: { senderId } },
        }),
      ) as MobileSender;
    },
    async addToJob(tenant, jobId, email) {
      unwrap(
        await client.POST('/jobs/{jobId}/mobile/senders', {
          params: { header: tenantHeader(tenant), path: { jobId } },
          body: { people: [{ email }] },
        }),
      );
    },
    async signOutPhone(tenant, senderId, phoneId) {
      unwrap(
        await client.DELETE('/company/mobile-senders/{senderId}/phones/{phoneId}', {
          params: { header: tenantHeader(tenant), path: { senderId, phoneId } },
        }),
      );
    },
  };
}

/** The two pages': the admin's sign-in for the app, and the invite page's hand-off. */
function pageCalls(client: PaigeClient): Pick<MobileApi, 'connectApp' | 'handOff'> {
  return {
    async connectApp(tenant, start) {
      return unwrap(
        await client.POST('/mobile/admin/connect', {
          params: { header: tenantHeader(tenant) },
          body: start,
        }),
      ) as DeviceConnectCode;
    },
    async handOff(token) {
      return unwrap(
        await client.POST('/mobile/invites/handoff', { body: { token } }),
      ) as MobileInviteHandoff;
    },
  };
}
